import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ClientsTableDocument,
  ContactFragment,
  FinanceValueObjectsContactTypeEnum,
  QuickCreateFinanceContactDocument,
  VendorsTableDocument,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { countries } from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ContactTaxIdTypeSelector } from 'modules/finance/activities/financeContactCreate/_ContactTaxIdTypeSelector'

import { ContactQuickAddFormData } from './ContactQuickAddFormData'

interface Props {
  onSuccessAndRedirect?: (contact: ContactFragment) => void
  contactType: FinanceValueObjectsContactTypeEnum
}

const ContactQuickAddForm = ({ onSuccessAndRedirect, contactType }: Props) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const { company } = useCurrentSession()

  const [quickCreateContact] = useMutation(QuickCreateFinanceContactDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceContact,
  })

  const {
    values,
    setErrors,
    setValues,
    isValid,
    dirty,
    errors: formErrors,
  } = useForm<ContactQuickAddFormData>({
    enableReinitialize: true,
    defaultValues: {
      name: '',
      countryCode: '',
      phoneNumber: '',
      website: '',
      taxId: '',
      taxIdType: undefined,
    },

    validate: (data) => {
      const errors: FormErrors<ContactQuickAddFormData> = {}

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },

    onSubmit: async () => {
      return { success: false, errors: [] }
    },
  })

  const handleSubmitAndRedirect = async () => {
    if (!isValid || submitting) return

    setSubmitting(true)

    const variables = {
      contactTypes: [contactType],
      companyId: company.id,
      name: values.name,
      countryCode: values.countryCode || '',
      website: values.website || '',
      phoneNumber: values.phoneNumber || '',
      taxId: values.taxId || '',
      taxIdType: values.taxIdType || undefined,
    }

    const result = await quickCreateContact({
      variables,
      awaitRefetchQueries: true,
      refetchQueries: [ClientsTableDocument, VendorsTableDocument],
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        setErrors(errors)
        app.setFlash({
          type: 'wrong',
          message:
            errors.errors || i18n.t('finance.contacts.contact_form.new_contact.messages.error'),
        })
      }
    })

    setSubmitting(false)

    if (handled.success) {
      const createdContact = handled.data?.contact
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.contacts.contact_form.update_contact.messages.success'),
      })

      track(trackingEvents.accounting.createContact)

      if (createdContact) {
        onSuccessAndRedirect?.(createdContact)
      }
    }
  }

  return (
    <>
      <Box gap="s24">
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.name')}
          value={values.name}
          onChange={(value) => setValues({ ...values, name: value })}
          mandatory
        />
        <Select
          none={i18n.t('common.select')}
          value={values.countryCode?.toLowerCase()}
          label={i18n.t('finance.contacts.contact_form.fields.country')}
          options={countries(i18n.locale)}
          onChange={(value) => setValues({ ...values, countryCode: value })}
        />
        <ContactTaxIdTypeSelector
          selectedTaxIdType={values.taxIdType}
          onSelectTaxIdType={(value) => setValues({ ...values, taxIdType: value })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.tax_identification_number')}
          value={values.taxId}
          onChange={(value) => {
            setValues({ ...values, taxId: value })
          }}
          error={formErrors.taxId}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.phone_number')}
          value={values.phoneNumber}
          onChange={(value) => setValues({ ...values, phoneNumber: value })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.website')}
          value={values.website}
          onChange={(value) => setValues({ ...values, website: value })}
        />
        {dirty && (
          <Box alignItems="flexEnd">
            <Button onClick={handleSubmitAndRedirect} disabled={!isValid || submitting}>
              {i18n.t('common.submit')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}

export default ContactQuickAddForm
